<template>
	<v-card class="hourly-rates-editor elevation-0 mb mb-4" outlined>
		<v-card-title class="pa-3 background-1">
			<v-icon color="primary" class="mr-2">mdi-clock-outline</v-icon>
			<span class="subtitle-1 font-weight-medium">{{ field.label }}</span>
			<v-spacer></v-spacer>
			<v-tooltip bottom>
				<template v-slot:activator="{ on }">
					<v-btn icon v-on="on" class="mr-2" @click="loadDefaultConfig" :disabled="!hasDefaultConfig">
						<v-icon color="primary">mdi-cog-transfer-outline</v-icon>
					</v-btn>
				</template>
				<span>Carica configurazione di default</span>
			</v-tooltip>
			<v-tooltip bottom>
				<template v-slot:activator="{ on }">
					<v-btn icon v-on="on" @click="copyConfigDialog = true">
						<v-icon color="primary">mdi-content-copy</v-icon>
					</v-btn>
				</template>
				<span>Copia configurazione tra giorni</span>
			</v-tooltip>
		</v-card-title>

		<v-divider></v-divider>

		<v-card-text class="pa-0">
			<v-tabs v-model="currentTab"
				background-color="background-1"
				color="primary"
				show-arrows
				fixed-tabs>

				<v-tab v-for="day in days" :key="day.key">
					<v-icon left :color="dayIconColor(day.key)">
						{{ day.icon }}
					</v-icon>
					{{ day.label }}
					<v-chip v-if="getRatesCount(day.key) > 0"
						x-small
						class="ml-2"
						color="primary">
						{{ getRatesCount(day.key) }}
					</v-chip>
				</v-tab>
			</v-tabs>

			<v-tabs-items v-model="currentTab" class="background-2">
				<v-tab-item v-for="day in days" :key="day.key">
					<v-card flat class="background-2">
						<v-card-text class="pa-4 pb-2">

							<!-- Header giorno -->
							<div class="d-flex align-center mb-4">
								<div>
									<h3 class="text-h6">{{ day.label }}</h3>
									<p class="caption grey--text mb-0">
										Configura le fasce orarie per {{ day.label.toLowerCase() }}
									</p>
								</div>
								<v-spacer></v-spacer>
								<v-btn color="primary"
									@click="createStagingSlot(day.key)"
									small
									:disabled="hasStagingSlot(day.key)">
									<v-icon left small>mdi-plus</v-icon>
									Aggiungi Fascia
								</v-btn>
							</div>

							<!-- Area di parcheggio (staging) -->
							<v-card v-if="hasStagingSlot(day.key)"
								class="mb-3 background-1"
								outlined>
								<v-card-text class="pa-3">
									<div class="d-flex align-center mb-2">
										<v-icon color="warning" class="mr-2">mdi-clock-plus-outline</v-icon>
										<span class="font-weight-medium">
											{{ isEditingStaging(day.key) ? 'Modifica Fascia Oraria' : 'Nuova Fascia Oraria' }}
										</span>
										<v-spacer></v-spacer>
										<v-btn icon
											x-small
											@click="cancelStagingSlot(day.key)">
											<v-icon small>mdi-close</v-icon>
										</v-btn>
									</div>

									<v-alert
										dense
										text
										color="info"
										icon="mdi-information-outline"
										class="ma-2 mb-3 caption">
										<strong>Suggerimento:</strong> Per una fascia che copre l'intera giornata usa <strong>00:00 - 24:00</strong>.
										Fasce consecutive si scrivono con lo stesso orario: <strong>06:00 - 22:00</strong> e <strong>22:00 - 24:00</strong>
									</v-alert>

									<v-row class="ma-0">
										<v-col cols="12" md="4" class="px-2 py-1">
											<v-text-field
												v-model="stagingSlots[day.key].label"
												label="Descrizione"
												outlined
												dense
												hide-details="auto"
												placeholder="es: Mattina, Pomeriggio..."
												:error-messages="getStagingValidationError(day.key, 'label')"
											></v-text-field>
										</v-col>

										<v-col cols="6" md="2" class="px-2 py-1">
											<v-text-field
												v-model="stagingSlots[day.key].time_from"
												label="Dalle"
												outlined
												dense
												hide-details="auto"
												v-mask="'##:##'"
												placeholder="HH:MM"
												:error-messages="getStagingValidationError(day.key, 'time_from')"
												@input="validateStagingSlot(day.key)"
											></v-text-field>
										</v-col>

										<v-col cols="6" md="2" class="px-2 py-1">
											<v-text-field
												v-model="stagingSlots[day.key].time_to"
												label="Alle"
												outlined
												dense
												hide-details="auto"
												v-mask="'##:##'"
												placeholder="HH:MM"
												:error-messages="getStagingValidationError(day.key, 'time_to')"
												@input="validateStagingSlot(day.key)"
											></v-text-field>
										</v-col>

										<v-col cols="12" md="4" class="px-2 py-1">
											<v-text-field
												v-model.number="stagingSlots[day.key].rate"
												:label="field.rate_label || 'Tariffa'"
												:suffix="field.rate_suffix || '€/h'"
												type="number"
												outlined
												dense
												hide-details="auto"
												:error-messages="getStagingValidationError(day.key, 'rate')"
												@input="validateStagingSlot(day.key)"
											></v-text-field>
										</v-col>
									</v-row>

									<div class="d-flex justify-end mt-2">
										<v-btn
											color="success"
											small
											@click="saveStagingSlot(day.key)"
											:disabled="!canAddStagingSlot(day.key)">
											<v-icon left small>mdi-check</v-icon>
											{{ isEditingStaging(day.key) ? 'Salva' : 'Aggiungi' }}
										</v-btn>
									</div>
								</v-card-text>
							</v-card>

							<!-- Lista fasce orarie -->
							<div v-if="config[day.key] && config[day.key].length > 0" class="mb-3">
								<v-card
									v-for="(rate, index) in config[day.key]"
									:key="`${day.key}_${index}`"
									class="mb-2 background-1"
									:class="{ 'editing-rate': isEditingStaging(day.key) && stagingEditIndex[day.key] === index }"
									outlined>
									<v-card-text class="pa-3">
										<div class="d-flex align-center">
											<v-icon color="primary" class="mr-3">mdi-clock-time-four-outline</v-icon>
											<div class="flex-grow-1">
												<div class="font-weight-medium">
													{{ rate.label || 'Fascia senza nome' }}
												</div>
												<div class="caption grey--text">
													{{ rate.time_from }} - {{ rate.time_to }}
													<span v-if="rate.rate" class="ml-2 primary--text font-weight-medium">
														{{ rate.rate }} {{ field.rate_suffix || '€/h' }}
													</span>
												</div>
											</div>
											<v-btn icon
												small
												color="primary"
												@click="editRate(day.key, index)"
												:disabled="hasStagingSlot(day.key)"
												class="mr-2">
												<v-icon small>mdi-pencil</v-icon>
											</v-btn>
											<v-btn icon
												small
												color="error"
												@click="deleteRate(day.key, index)"
												:disabled="hasStagingSlot(day.key)">
												<v-icon small>mdi-delete</v-icon>
											</v-btn>
										</div>
									</v-card-text>
								</v-card>

								<v-alert v-if="uncoveredByDay[day.key].length"
									dense
									text
									:type="uncoveredNotBilled ? 'warning' : 'info'"
									class="mt-3 mb-0 caption">
									Nessuna fascia copre <strong>{{ uncoveredByDay[day.key].join(', ') }}</strong>:
									<template v-if="uncoveredNotBilled">le ore lavorate in questi orari non vengono fatturate (0 €).</template>
									<template v-else>in questi orari vale la tariffa oraria base.</template>
								</v-alert>
							</div>

							<!-- Empty state -->
							<v-card v-else flat class="mb-2 background-1 text-center pa-6">
								<v-icon size="64" color="grey lighten-1">mdi-clock-outline</v-icon>
								<p class="grey--text mt-3 mb-0">
									Nessuna fascia oraria configurata per {{ day.label.toLowerCase() }}
								</p>
								<v-btn color="primary"
									class="mt-3"
									small
									outlined
									@click="createStagingSlot(day.key)">
									<v-icon left small>mdi-plus</v-icon>
									Aggiungi Prima Fascia
								</v-btn>
							</v-card>

							<v-alert v-if="emptyDayNotBilled(day.key)"
								dense
								text
								type="warning"
								class="mb-0 caption">
								<template v-if="day.key === 'holidays'">
									Senza fasce le ore lavorate nei giorni festivi non vengono fatturate (0 €),
									anche se il festivo cade in un giorno che ha le sue fasce.
								</template>
								<template v-else>
									Senza fasce le ore lavorate di {{ day.label.toLowerCase() }} non vengono fatturate (0 €).
								</template>
							</v-alert>

						</v-card-text>
					</v-card>
				</v-tab-item>
			</v-tabs-items>
		</v-card-text>

		<!-- Dialog copia configurazione -->
		<v-dialog v-model="copyConfigDialog" max-width="600" class="round-card" persistent>
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3 background-1">
					<v-icon color="primary" class="mr-2">mdi-content-copy</v-icon>
					<p>
						Copia Configurazione
					</p>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pa-4">
					<p class="mb-3">Copia le fasce orarie da un giorno ad altri giorni</p>

					<v-select
						v-model="copyFromDay"
						:items="daysWithRates"
						label="Copia da"
						outlined
						dense
						item-text="label"
						item-value="key"
						class="mb-3"
						:menu-props="{'offset-y': true}"
					></v-select>

					<v-select
						v-model="copyToDays"
						:items="getAvailableDaysForCopy"
						label="Incolla in"
						outlined
						dense
						multiple
						chips
						small-chips
						item-text="label"
						item-value="key"
						:menu-props="{'offset-y': true}"
					>
						<template v-slot:selection="{ item, index }">
							<v-chip v-if="index < 3" small>{{ item.label }}</v-chip>
							<span v-if="index === 3" class="grey--text caption ml-1">
								(+{{ copyToDays.length - 3 }} altri)
							</span>
						</template>
					</v-select>

					<v-alert type="warning"
						dense
						outlined
						class="mb-0"
						v-if="copyToDays.length > 0">
						Le configurazioni esistenti nei giorni selezionati verranno sovrascritte
					</v-alert>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions class="pa-2">
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="closeCopyDialog">Annulla</v-btn>
					<v-btn text
						color="info"
						@click="executeCopy"
						:disabled="!copyFromDay || copyToDays.length === 0">
						Copia
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

	</v-card>
</template>

<script>
import Vue from 'vue'

export default {
	name: 'hourly-rates-editor',

	props: {
		field: {
			type: Object,
			required: true
		},
		model: {
			type: Object,
			required: true
		}
	},

	data() {
		return {
			currentTab: 0,

			days: [
				{ key: 'monday', label: 'Lunedì', icon: 'mdi-calendar' },
				{ key: 'tuesday', label: 'Martedì', icon: 'mdi-calendar' },
				{ key: 'wednesday', label: 'Mercoledì', icon: 'mdi-calendar' },
				{ key: 'thursday', label: 'Giovedì', icon: 'mdi-calendar' },
				{ key: 'friday', label: 'Venerdì', icon: 'mdi-calendar' },
				{ key: 'saturday', label: 'Sabato', icon: 'mdi-calendar-weekend' },
				{ key: 'sunday', label: 'Domenica', icon: 'mdi-calendar-weekend' },
				{ key: 'holidays', label: 'Festivi', icon: 'mdi-party-popper' }
			],

			config: {},

			validationErrors: {},

			// Staging area per nuove fasce orarie (una per giorno)
			stagingSlots: {},

			// Traccia se lo slot in staging è una modifica (contiene indice originale)
			stagingEditIndex: {},

			// Copy dialog
			copyConfigDialog: false,
			copyFromDay: null,
			copyToDays: [],

			// Settings di default caricati dallo store
			defaultSaleConfig: null,
			defaultCostConfig: null,

			// Flag per evitare loop nel watcher
			isInitializing: false
		}
	},

	computed: {
		daysWithRates() {
			return this.days.filter(day => this.hasRates(day.key))
		},

		getAvailableDaysForCopy() {
			return this.days.filter(day => day.key !== this.copyFromDay)
		},

		hasValidationErrors() {
			return Object.keys(this.validationErrors).length > 0
		},

		validationErrorsCount() {
			return Object.keys(this.validationErrors).length
		},

		// Orari del giorno non coperti da nessuna fascia, letti come il calcolo del backend
		// (HourlyRateCalculatorService::effectiveBands): una fascia che finisce alle 23:59
		// o un minuto prima dell'inizio di un'altra arriva fino a quell'inizio
		uncoveredByDay() {
			const result = {}
			this.days.forEach(day => {
				result[day.key] = this.uncoveredRanges(this.config[day.key] || [])
			})
			return result
		},

		// Sul tariffario un orario senza fascia vale 0 €; per utenti e contractor vale la tariffa oraria base
		uncoveredNotBilled() {
			return this.field.uncovered_rate === 'zero'
		},

		hasAnyRates() {
			return this.days.some(day => this.hasRates(day.key))
		},

		hasDefaultConfig() {
			const defaultConfig = this.field.rate_type === 'sale'
				? this.defaultSaleConfig
				: this.defaultCostConfig

			return defaultConfig && typeof defaultConfig === 'object' && Object.keys(defaultConfig).length > 0
		}
	},

	watch: {
		config: {
			handler(newVal) {
				// Aggiorna il model del parent solo se non stiamo inizializzando
				if (!this.isInitializing) {
					Vue.set(this.model, this.field.key, newVal)
				}
			},
			deep: true
		},

		currentTab(newVal, oldVal) {
			// Ordina le fasce orarie del giorno precedente quando si cambia tab
			// Questo evita il riordinamento durante l'editing
			if (oldVal !== null && oldVal !== undefined && this.days[oldVal]) {
				const oldDay = this.days[oldVal]
				this.sortRates(oldDay.key)
			}
		},

		// Watcher per ricaricare dati quando vengono popolati dal backend
		model: {
			handler(newVal) {
				const newConfig = newVal[this.field.key]
				if (newConfig && typeof newConfig === 'object' && Object.keys(newConfig).length > 0) {
					// Controlla se config è ancora vuoto (prima inizializzazione)
					const currentConfigEmpty = Object.keys(this.config).every(
						key => !this.config[key] || this.config[key].length === 0
					)

					if (currentConfigEmpty && !this.isInitializing) {
						this.initializeConfig()
					}
				}
			},
			deep: true,
			immediate: false
		}
	},

	mounted() {
		this.initializeConfig()
		this.loadDefaultSettings()
	},

	methods: {
		loadDefaultSettings() {
			// Carica i settings di default dallo store
			this.defaultSaleConfig = this.$store.getters.getSetting('default_hourly_rates_sale_config')
			this.defaultCostConfig = this.$store.getters.getSetting('default_hourly_rates_cost_config')
		},

		initializeConfig() {
			// Imposta flag per evitare loop nei watcher
			this.isInitializing = true

			// Inizializza la configurazione dal model o crea una nuova
			const existingConfig = this.model[this.field.key]

			if (existingConfig && typeof existingConfig === 'object') {
				this.config = { ...existingConfig }
			} else {
				this.config = {}
			}

			// Assicurati che ogni giorno abbia almeno un array vuoto
			this.days.forEach(day => {
				if (!this.config[day.key]) {
					Vue.set(this.config, day.key, [])
				}
			})

			// Reset flag dopo l'inizializzazione
			this.$nextTick(() => {
				this.isInitializing = false
			})
		},

		createStagingSlot(dayKey) {
			Vue.set(this.stagingSlots, dayKey, {
				label: '',
				time_from: '',
				time_to: '',
				rate: null
			})
			// Nuova fascia, non è una modifica
			Vue.delete(this.stagingEditIndex, dayKey)
		},

		hasStagingSlot(dayKey) {
			return this.stagingSlots[dayKey] !== undefined
		},

		isEditingStaging(dayKey) {
			return this.stagingEditIndex[dayKey] !== undefined
		},

		editRate(dayKey, index) {
			// Non permettere modifica se c'è già una fascia in staging
			if (this.hasStagingSlot(dayKey)) {
				return
			}

			// Copia la fascia da modificare nello staging
			const rateToEdit = { ...this.config[dayKey][index] }
			Vue.set(this.stagingSlots, dayKey, rateToEdit)

			// Salva l'indice originale per sapere quale fascia stiamo modificando
			Vue.set(this.stagingEditIndex, dayKey, index)

			// NON rimuovere dalla config, rimane visibile come read-only
		},

		cancelStagingSlot(dayKey) {
			// Se stava modificando, pulisci solo l'indice (la fascia originale è ancora in config)
			if (this.isEditingStaging(dayKey)) {
				Vue.delete(this.stagingEditIndex, dayKey)
			}

			Vue.delete(this.stagingSlots, dayKey)
			this.clearStagingValidationErrors(dayKey)
		},

		canAddStagingSlot(dayKey) {
			const slot = this.stagingSlots[dayKey]
			if (!slot) return false

			// Verifica che tutti i campi siano compilati
			const allFieldsFilled = slot.label &&
									slot.time_from &&
									slot.time_to &&
									slot.rate !== null &&
									slot.rate !== ''

			if (!allFieldsFilled) return false

			// Verifica che non ci siano errori di validazione
			const hasNoErrors = !this.hasStagingValidationErrors(dayKey)

			return hasNoErrors
		},

		saveStagingSlot(dayKey) {
			const slot = this.stagingSlots[dayKey]
			if (!slot || !this.canAddStagingSlot(dayKey)) return

			const isEditing = this.isEditingStaging(dayKey)

			// Aggiungi/Aggiorna alla configurazione
			if (!this.config[dayKey]) {
				Vue.set(this.config, dayKey, [])
			}

			if (isEditing) {
				// Sostituisci la fascia esistente con quella modificata
				const originalIndex = this.stagingEditIndex[dayKey]
				Vue.set(this.config[dayKey], originalIndex, { ...slot })
			} else {
				// Aggiungi nuova fascia
				this.config[dayKey].push({ ...slot })
			}

			// Ordina le fasce orarie
			this.sortRates(dayKey)

			// Pulisci staging e indice di modifica
			Vue.delete(this.stagingSlots, dayKey)
			Vue.delete(this.stagingEditIndex, dayKey)
			this.clearStagingValidationErrors(dayKey)

			// Mostra messaggio di successo
			this.$store.commit('setSnackbar', {
				timeout: 3000,
				text: isEditing ? 'Fascia oraria modificata con successo' : 'Fascia oraria aggiunta con successo',
				value: true,
				color: 'success'
			})
		},

		validateStagingSlot(dayKey) {
			const slot = this.stagingSlots[dayKey]
			if (!slot) return

			this.clearStagingValidationErrors(dayKey)

			// Valida formato ora
			if (slot.time_from && !this.isValidTime(slot.time_from)) {
				this.addStagingValidationError(dayKey, 'time_from', 'Formato ora non valido (HH:MM)')
			}
			if (slot.time_to && !this.isValidTime(slot.time_to)) {
				this.addStagingValidationError(dayKey, 'time_to', 'Formato ora non valido (HH:MM)')
			}

			// Valida che time_to > time_from
			if (slot.time_from && slot.time_to && this.isValidTime(slot.time_from) && this.isValidTime(slot.time_to)) {
				if (slot.time_to <= slot.time_from) {
					this.addStagingValidationError(dayKey, 'time_to', 'L\'orario di fine deve essere successivo all\'orario di inizio')
				}
			}

			// Verifica sovrapposizioni con fasce esistenti
			if (slot.time_from && slot.time_to && this.config[dayKey]) {
				this.config[dayKey].forEach((existingRate, index) => {
					if (!existingRate.time_from || !existingRate.time_to) return

					// Se stiamo modificando una fascia esistente, escludi il confronto con se stessa
					if (this.isEditingStaging(dayKey) && index === this.stagingEditIndex[dayKey]) {
						return
					}

					if (this.timeSlotsOverlap(slot, existingRate)) {
						this.addStagingValidationError(
							dayKey,
							'time_from',
							`Sovrapposizione con fascia esistente: ${existingRate.time_from}-${existingRate.time_to}`
						)
					}
				})
			}
		},

		hasStagingValidationErrors(dayKey) {
			return Object.keys(this.validationErrors).some(key => key.startsWith(`staging_${dayKey}_`))
		},

		addStagingValidationError(dayKey, field, message) {
			const key = `staging_${dayKey}_${field}`
			if (!this.validationErrors[key]) {
				Vue.set(this.validationErrors, key, [])
			}
			if (!this.validationErrors[key].includes(message)) {
				this.validationErrors[key].push(message)
			}
		},

		clearStagingValidationErrors(dayKey) {
			Object.keys(this.validationErrors).forEach(key => {
				if (key.startsWith(`staging_${dayKey}_`)) {
					Vue.delete(this.validationErrors, key)
				}
			})
		},

		getStagingValidationError(dayKey, field) {
			const key = `staging_${dayKey}_${field}`
			return this.validationErrors[key] || []
		},

		deleteRate(dayKey, index) {
			if (this.config[dayKey]) {
				this.config[dayKey].splice(index, 1)
				this.clearValidationErrors(dayKey, index)
			}
		},

		uncoveredRanges(rates) {
			const bands = rates
				.filter(rate => this.isValidTime(rate.time_from) && this.isValidTime(rate.time_to))
				.map(rate => ({ from: this.timeToMinutes(rate.time_from), to: this.timeToMinutes(rate.time_to) }))
			if (bands.length === 0) return []

			const starts = bands.map(band => band.from)
			const intervals = []
			bands.forEach(band => {
				const to = (band.to === 1439 || starts.includes(band.to + 1)) ? band.to + 1 : band.to
				if (to > band.from) {
					intervals.push([band.from, to])
				} else if (to < band.from) {
					// A cavallo della mezzanotte (22:00 - 06:00)
					intervals.push([band.from, 1440], [0, to])
				}
			})
			intervals.sort((a, b) => a[0] - b[0])

			const format = minutes => String(Math.floor(minutes / 60)).padStart(2, '0') + ':' + String(minutes % 60).padStart(2, '0')
			const gaps = []
			let coveredUntil = 0
			intervals.forEach(([from, to]) => {
				if (from > coveredUntil) gaps.push(format(coveredUntil) + ' - ' + format(from))
				coveredUntil = Math.max(coveredUntil, to)
			})
			if (coveredUntil < 1440) gaps.push(format(coveredUntil) + ' - ' + format(1440))
			return gaps
		},

		// Giorno senza fasce quando altri giorni ne hanno: sul tariffario quel giorno si fattura 0 €
		emptyDayNotBilled(dayKey) {
			return this.uncoveredNotBilled && this.hasAnyRates && !this.hasRates(dayKey)
		},

		dayIconColor(dayKey) {
			if (!this.hasRates(dayKey)) return this.emptyDayNotBilled(dayKey) ? 'warning' : 'grey'
			return this.uncoveredNotBilled && this.uncoveredByDay[dayKey].length ? 'warning' : 'success'
		},

		hasRates(dayKey) {
			return this.config[dayKey] && this.config[dayKey].length > 0
		},

		getRatesCount(dayKey) {
			return this.config[dayKey] ? this.config[dayKey].length : 0
		},

		sortRates(dayKey) {
			if (!this.config[dayKey]) return

			this.config[dayKey].sort((a, b) => {
				const timeA = a.time_from || '00:00'
				const timeB = b.time_from || '00:00'
				return timeA.localeCompare(timeB)
			})
		},

		validateTimeSlots(dayKey) {
			if (!this.config[dayKey]) return

			this.clearDayValidationErrors(dayKey)
			const rates = this.config[dayKey]

			rates.forEach((rate, index) => {
				// Valida formato ora
				if (rate.time_from && !this.isValidTime(rate.time_from)) {
					this.addValidationError(dayKey, index, 'time_from', 'Formato ora non valido (HH:MM)')
				}
				if (rate.time_to && !this.isValidTime(rate.time_to)) {
					this.addValidationError(dayKey, index, 'time_to', 'Formato ora non valido (HH:MM)')
				}

				// Valida che time_to > time_from
				if (rate.time_from && rate.time_to && this.isValidTime(rate.time_from) && this.isValidTime(rate.time_to)) {
					if (rate.time_to <= rate.time_from) {
						this.addValidationError(dayKey, index, 'time_to', 'L\'orario di fine deve essere successivo all\'orario di inizio')
					}
				}

				// Verifica sovrapposizioni
				rates.forEach((otherRate, otherIndex) => {
					if (index === otherIndex) return
					if (!otherRate.time_from || !otherRate.time_to) return
					if (!rate.time_from || !rate.time_to) return

					if (this.timeSlotsOverlap(rate, otherRate)) {
						this.addValidationError(
							dayKey,
							index,
							'time_from',
							`Sovrapposizione con fascia ${otherIndex + 1}: ${otherRate.time_from}-${otherRate.time_to}`
						)
					}
				})
			})

			// Non ordinare durante l'editing per evitare perdita di focus
			// L'ordinamento avviene quando si cambia tab o si salva
		},

		isValidTime(time) {
			if (!time) return false
			// Accetta 24:00 come caso speciale per rappresentare la fine della giornata (mezzanotte)
			if (time === '24:00') return true

			const regex = /^([0-1][0-9]|2[0-3]):([0-5][0-9])$/
			if (!regex.test(time)) return false

			const [hours, minutes] = time.split(':').map(Number)
			return hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60
		},

		// Converte HH:MM in minuti (24:00 viene convertito in 1440 minuti = 24*60)
		timeToMinutes(time) {
			const [h, m] = time.split(':').map(Number)
			return h * 60 + m
		},

		timeSlotsOverlap(slot1, slot2) {
			const start1 = this.timeToMinutes(slot1.time_from)
			const end1 = this.timeToMinutes(slot1.time_to)
			const start2 = this.timeToMinutes(slot2.time_from)
			const end2 = this.timeToMinutes(slot2.time_to)

			// Verifica sovrapposizione
			return (start1 < end2 && end1 > start2)
		},

		addValidationError(dayKey, index, field, message) {
			const key = `${dayKey}_${index}_${field}`
			if (!this.validationErrors[key]) {
				Vue.set(this.validationErrors, key, [])
			}
			if (!this.validationErrors[key].includes(message)) {
				this.validationErrors[key].push(message)
			}
		},

		clearValidationErrors(dayKey, index) {
			Object.keys(this.validationErrors).forEach(key => {
				if (key.startsWith(`${dayKey}_${index}_`)) {
					Vue.delete(this.validationErrors, key)
				}
			})
		},

		clearDayValidationErrors(dayKey) {
			Object.keys(this.validationErrors).forEach(key => {
				if (key.startsWith(`${dayKey}_`)) {
					Vue.delete(this.validationErrors, key)
				}
			})
		},

		getValidationError(dayKey, index, field) {
			const key = `${dayKey}_${index}_${field}`
			return this.validationErrors[key] || []
		},

		// Copy functionality
		executeCopy() {
			if (!this.copyFromDay || this.copyToDays.length === 0) return

			const sourceRates = JSON.parse(JSON.stringify(this.config[this.copyFromDay] || []))

			this.copyToDays.forEach(targetDay => {
				Vue.set(this.config, targetDay, JSON.parse(JSON.stringify(sourceRates)))
			})

			this.$store.commit('setSnackbar', {
				timeout: 4000,
				text: `Configurazione copiata in ${this.copyToDays.length} giorni`,
				value: true,
				color: 'success'
			})

			this.closeCopyDialog()
		},

		closeCopyDialog() {
			this.copyConfigDialog = false
			this.copyFromDay = null
			this.copyToDays = []
		},

		loadDefaultConfig() {
			// Determina quale setting usare in base al rate_type
			const defaultConfig = this.field.rate_type === 'sale'
				? this.defaultSaleConfig
				: this.defaultCostConfig

			if (!defaultConfig || typeof defaultConfig !== 'object') {
				this.$store.commit('setSnackbar', {
					timeout: 4000,
					text: 'Nessuna configurazione di default disponibile',
					value: true,
					color: 'warning'
				})
				return
			}

			// Copia la configurazione di default in config
			this.isInitializing = true

			this.days.forEach(day => {
				if (defaultConfig[day.key] && Array.isArray(defaultConfig[day.key])) {
					this.$set(this.config, day.key, JSON.parse(JSON.stringify(defaultConfig[day.key])))
				} else {
					this.$set(this.config, day.key, [])
				}
			})

			this.$nextTick(() => {
				this.isInitializing = false
				// Aggiorna manualmente il model dopo che isInitializing è false
				this.$set(this.model, this.field.key, this.config)
			})

			this.$store.commit('setSnackbar', {
				timeout: 4000,
				text: 'Configurazione di default caricata con successo',
				value: true,
				color: 'success'
			})
		},

		validateAllDays() {
			// Valida tutti i giorni per trovare eventuali errori nascosti
			this.days.forEach(day => {
				if (this.config[day.key] && this.config[day.key].length > 0) {
					this.validateTimeSlots(day.key)
				}
			})
		},

		// Metodo pubblico per ordinare tutte le fasce prima del salvataggio
		// Può essere chiamato dal form parent con this.$refs.hourlyRatesEditor.sortAllRates()
		sortAllRates() {
			this.days.forEach(day => {
				this.sortRates(day.key)
			})
		},

		// Metodo pubblico chiamato dal form parent prima del salvataggio
		// Ritorna true se non ci sono errori, false altrimenti
		validate() {
			// Ordina tutte le fasce
			this.sortAllRates()

			// Valida tutti i giorni
			this.validateAllDays()

			// Ritorna true se non ci sono errori
			return !this.hasValidationErrors
		}
	}
}
</script>

<style scoped>
.hourly-rates-editor {
	border-radius: 8px;
	overflow: hidden;
}

.v-expansion-panel::before {
	box-shadow: none !important;
}

.v-tab {
	text-transform: none !important;
	letter-spacing: normal !important;
}

.editing-rate {
	border: 2px solid #FFA726 !important;
	background-color: rgba(255, 167, 38, 0.05) !important;
}
</style>
